import { useState } from 'react';

export interface FilterState {
  genre: string;
  region: string;
  year: string;
  sort: 'update' | 'hot';
}

interface FilterBarProps {
  genres: string[];
  regions: string[];
  years: string[];
  value: FilterState;
  onChange: (next: Partial<FilterState>) => void;
  onReset: () => void;
}

const ChipRow = ({
  label,
  options,
  active,
  onSelect,
}: {
  label: string;
  options: string[];
  active: string;
  onSelect: (v: string) => void;
}) => (
  <div className="flex items-start gap-3 py-2.5 border-b border-background-200 last:border-0">
    <span className="w-12 shrink-0 pt-1 text-xs md:text-sm text-foreground-500">{label}</span>
    <div className="flex flex-wrap gap-2">
      {['全部', ...options].map((opt) => (
        <button
          key={opt}
          type="button"
          onClick={() => onSelect(opt)}
          className={`px-3 py-1 rounded-full text-xs md:text-sm transition-colors cursor-pointer whitespace-nowrap ${
            active === opt
              ? 'bg-accent-500 text-secondary-950 font-medium'
              : 'bg-background-100 text-foreground-600 hover:bg-background-200'
          }`}
        >
          {opt}
        </button>
      ))}
    </div>
  </div>
);

export default function FilterBar({ genres, regions, years, value, onChange, onReset }: FilterBarProps) {
  const [open, setOpen] = useState(false);

  const body = (
    <div>
      <ChipRow label="类型" options={genres} active={value.genre} onSelect={(v) => onChange({ genre: v })} />
      <ChipRow label="地区" options={regions} active={value.region} onSelect={(v) => onChange({ region: v })} />
      <ChipRow label="年份" options={years} active={value.year} onSelect={(v) => onChange({ year: v })} />

      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 pt-3">
        <div className="flex items-center gap-2">
          <span className="text-xs md:text-sm text-foreground-500">排序</span>
          <div className="flex items-center bg-background-100 rounded-full p-1">
            <button
              type="button"
              onClick={() => onChange({ sort: 'update' })}
              className={`px-3 py-1 rounded-full text-xs md:text-sm transition-colors cursor-pointer whitespace-nowrap ${
                value.sort === 'update'
                  ? 'bg-primary-600 text-background-50 font-medium'
                  : 'text-foreground-600'
              }`}
            >
              按更新时间
            </button>
            <button
              type="button"
              onClick={() => onChange({ sort: 'hot' })}
              className={`px-3 py-1 rounded-full text-xs md:text-sm transition-colors cursor-pointer whitespace-nowrap ${
                value.sort === 'hot'
                  ? 'bg-primary-600 text-background-50 font-medium'
                  : 'text-foreground-600'
              }`}
            >
              按热度
            </button>
          </div>
        </div>
        <button
          type="button"
          onClick={onReset}
          className="inline-flex items-center gap-1 px-4 h-9 rounded-md border border-background-300 text-sm text-foreground-700 hover:border-primary-300 hover:text-primary-700 transition-colors cursor-pointer whitespace-nowrap self-start sm:self-auto"
        >
          <i className="ri-refresh-line" aria-hidden="true"></i>
          重置筛选
        </button>
      </div>
    </div>
  );

  return (
    <div>
      {/* 移动端：筛选按钮 + 弹层 */}
      <div className="md:hidden">
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className="w-full h-11 rounded-md bg-background-100 border border-background-200 text-sm text-foreground-700 flex items-center justify-center gap-2 cursor-pointer"
        >
          <i className="ri-equalizer-line" aria-hidden="true"></i>
          {open ? '收起筛选条件' : '展开筛选条件'}
        </button>
        {open && (
          <div className="mt-3 p-4 rounded-lg bg-background-50 border border-background-200">{body}</div>
        )}
      </div>

      {/* 桌面端：内联筛选 */}
      <div className="hidden md:block p-5 rounded-lg bg-background-50 border border-background-200">
        {body}
      </div>
    </div>
  );
}